/**
 * What the Order dialog is about to buy, as data. Pure, so the arithmetic that
 * decides what somebody is charged is testable without a DOM or a server.
 *
 * Ported from `asthra-v4-web/src/new-document/order.ts` + `section-pricing.ts`,
 * but the add-in has no `GatePacket`/`PacketRow.rootNumber`: roots are derived
 * here from `DocTypeConfig.skeleton` (`[number, title, level][]`) and the
 * selected section numbers (`LoomSelectionCommit.resolvedSectionIds`, which are
 * skeleton numbers like "9.3").
 *
 * ## The rule, in one sentence
 *
 * A section is a numbered heading and everything under it, so ticking `9.1`
 * and `9.2` buys section `9`, once. The cart carries **root section numbers**
 * as `module_id`, which is what makes the server's claim key `doc#9` dedupe.
 */

import type { CheckoutItem, DocTypeConfig } from "../../api/client";
import type { DocPrice } from "./pricing";

export interface OrderLine {
  /** "9" for a section, `null` for a whole document. */
  readonly section: string | null;
  readonly label: string;
  readonly priceMinor: number;
}

export interface Order {
  readonly scope: "document" | "section";
  readonly lines: readonly OrderLine[];
  readonly totalMinor: number;
  readonly items: readonly CheckoutItem[];
}

/**
 * Why an order cannot be placed, when it cannot. Separate from a thrown error
 * because every one of these is a thing to *say* in the dialog, not a fault.
 */
export type OrderRefusal =
  | { readonly kind: "unpriced" }
  | { readonly kind: "sold-whole" }
  | { readonly kind: "nothing-selected" }
  /** Everything picked is already paid for — draft it, do not sell it again. */
  | { readonly kind: "all-covered" }
  | { readonly kind: "too-many-sections"; readonly count: number; readonly max: number };

/** The server's cart ceiling (`MAX_CART_ITEMS`, `server/billing/service.py`). */
export const MAX_CART_ITEMS = 20;

const NO_COVERED: ReadonlySet<string> = new Set();

/** Root section number of a skeleton id: the part before the first dot. */
export function rootOf(sectionId: string): string {
  const dot = sectionId.indexOf(".");
  return dot === -1 ? sectionId : sectionId.slice(0, dot);
}

/** The distinct roots a selection bills, in skeleton order. */
export function billedRoots(config: DocTypeConfig, selected: readonly string[]): readonly string[] {
  const picked = new Set(selected);
  const seen = new Set<string>();
  const roots: string[] = [];
  // Walk the skeleton so the order is the document's, not the click order.
  for (const [number] of config.skeleton ?? []) {
    if (!picked.has(number)) continue;
    const root = rootOf(number);
    if (seen.has(root)) continue;
    seen.add(root);
    roots.push(root);
  }
  return roots;
}

/** `billedRoots` minus whatever a collaborator has already paid for. */
export function billableRoots(
  config: DocTypeConfig,
  selected: readonly string[],
  covered: ReadonlySet<string>,
): readonly string[] {
  return billedRoots(config, selected).filter((root) => !covered.has(root));
}

/** How many distinct roots this doctype has to sell (for the whole-doc upsell). */
export function totalRootCount(config: DocTypeConfig): number {
  const roots = new Set<string>();
  for (const [number] of config.skeleton ?? []) {
    roots.add(rootOf(number));
  }
  return roots.size;
}

/**
 * Whether buying the whole document is worth offering here — i.e. buying every
 * section would cost more than the document. Declines to advertise an upsell
 * that would cost the reader money.
 */
export function wholeDocumentIsWorthOffering(config: DocTypeConfig, price: DocPrice | undefined): boolean {
  if (price === undefined || price.sectionMinor === null) return false;
  return totalRootCount(config) * price.sectionMinor > price.documentMinor;
}

/** Root section number → its heading, from the skeleton, for the order lines. */
function rootTitles(config: DocTypeConfig): ReadonlyMap<string, string> {
  const out = new Map<string, string>();
  for (const [number, title] of config.skeleton ?? []) {
    if (rootOf(number) === number) out.set(number, title);
  }
  return out;
}

export interface OrderInput {
  readonly config: DocTypeConfig;
  readonly docId: string;
  readonly docType: string;
  readonly price: DocPrice | undefined;
  /** Selected skeleton section numbers (`resolvedSectionIds`). */
  readonly selected: readonly string[];
  /** True when the buyer chose the whole document rather than a set of sections. */
  readonly whole: boolean;
  /** Root numbers a collaborator already paid for; excluded from bill and lines. */
  readonly covered?: ReadonlySet<string>;
}

/** The order, or the reason there isn't one. */
export function buildOrder(input: OrderInput): Order | OrderRefusal {
  const { config, docId, docType, price, selected, whole, covered = NO_COVERED } = input;

  // No price for this doctype (off the allowlist, or no provider keys). Not an
  // error — the caller drafts for free.
  if (price === undefined) return { kind: "unpriced" };

  if (whole) {
    return {
      scope: "document",
      lines: [{ section: null, label: "The whole document", priceMinor: price.documentMinor }],
      totalMinor: price.documentMinor,
      items: [{ doc_id: docId, doc_type: docType, scope: "document" }],
    };
  }

  // A sold-whole doctype has no section product, so there is no such order to
  // place — the caller offers the whole document instead.
  if (price.sectionMinor === null) return { kind: "sold-whole" };

  const roots = billableRoots(config, selected, covered);
  if (roots.length === 0 && selected.length > 0) return { kind: "all-covered" };
  if (roots.length === 0) return { kind: "nothing-selected" };
  if (roots.length > MAX_CART_ITEMS) {
    return { kind: "too-many-sections", count: roots.length, max: MAX_CART_ITEMS };
  }

  const sectionMinor = price.sectionMinor;
  const titles = rootTitles(config);
  return {
    scope: "section",
    lines: roots.map((section) => ({
      section,
      label: titles.get(section) ?? section,
      priceMinor: sectionMinor,
    })),
    totalMinor: sectionMinor * roots.length,
    items: roots.map((section) => ({
      doc_id: docId,
      doc_type: docType,
      scope: "section" as const,
      module_id: section,
    })),
  };
}

/** Narrowing helper — a refusal has a `kind`, an order has a `scope`. */
export function isRefusal(result: Order | OrderRefusal): result is OrderRefusal {
  return "kind" in result;
}
